import React, { useEffect } from "react"
import { graphql, Link } from "gatsby"
import Layout from "../components/layout"
import WavesImage from "../components/waves-image"
import Section from "../components/page-section"
import HeroSectionDark from "../components/hero-section-dark"
import { redirectIfNeeded } from "../utils/helpers"
import heroHome from "../assets/b2b-hero.svg"
import TestimonialsSection from "../components/testimonials-section"
import { RichText } from "prismic-reactjs"
import htmlSerializer from "../utils/html-serializer"
import FaqSection from "../components/faq-section"
import FeatureSection from "../components/feature-section"
import PartnersSection from "../components/partners-section"
import NetworkSection from "../components/network-section"
import { withPreview } from "gatsby-source-prismic"

const B2BTemplate = ({ data, pageContext, location }) => {
  const page = data?.prismicB2BHomepage.data
  if (!page) return null

  const { lang: locale } = pageContext
  useEffect(() => {
    redirectIfNeeded({ locale, location })
  }, [])

  // because of images take time to load we scroll to the element manually with a little delay https://miguelpiedrafita.com/snippets/scrollToHash
  useEffect(() => {
    const { hash } = location

    if (!hash) {
      return
    }
    setTimeout(() => {
      const anchor = document.querySelector(hash)
      const offset = anchor.getBoundingClientRect().top + window.scrollY

      window.scroll({ top: offset, left: 0 })
    }, 800)
  }, [])

  return (
    <Layout
      metadata={{
        title: page.meta_title,
        description: page.description_meta,
        keywords: page.meta_keywords,
        image: page.meta_image && page.meta_image.url,
      }}
      mainPage="companies"
      locale={locale}
      path={location && location.pathname}
    >
      <HeroSectionDark
        hero={{
          title: page.hero_title,
          subtitle: page.hero_subtitle,
          button: page.hero_button_text,
          link: page.hero_link,
        }}
        locale={locale}
        heroImage={heroHome}
        link="https://my.moberries.com/signup"
        page="home"
      />
      <WavesImage page="home" />
      <section className="py-6 text-center border-bottom">
        <div className="container">
          <div className="text-muted mb-4 mb-lg-5">{page.customers_title}</div>
          <PartnersSection partners={page.partners} />
        </div>
      </section>
      <Section title={page.suite_title} subtitle={page.suite_subtitle}>
        <div className="row">
          {page.suite_bubbles.map(item => (
            <div className="col-md d-flex flex-column" key={item.bubble_title}>
              <div className="my-3">
                <img
                  data-sal="fade"
                  data-sal-easing="ease-in-out"
                  data-sal-duration="1500"
                  alt="MoBerries Benefit"
                  height="160"
                  src={item.bubble_icon.url}
                />
              </div>
              <h4>{item.bubble_title}</h4>
              <p>{item.bubble_body}</p>
              <p className="mb-4 mt-auto">
                <Link to={item.link_url}>{item.link_text}</Link>
              </p>
            </div>
          ))}
        </div>
      </Section>
      <Section
        title={page.integrations_title}
        subtitle={page.integrations_subtitle}
      >
        <div className="row justify-content-center">
          {page.integrations_logos.map((item, i) => (
            <div className="col-md-3 mb-3" key={i}>
              <img
                className="img-fluid"
                alt="MoBerries ATS Integrations"
                src={item.logo.url}
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <a className="cta" href={page.pricing_button_url}>
            {page.pricing_button_text}
          </a>
        </div>
      </Section>
      <TestimonialsSection
        testimonials={{
          title: page.testimonials_title,
          items: page.testimonials,
        }}
      />
      <NetworkSection
        network={{
          title: page.network_title,
          sector_1: page.sector_1,
          sector_2: page.sector_2,
          sector_3: page.sector_3,
        }}
      />
      <Section title={page.pricing_title} id="pricing-section">
        <div className="row pt-4 justify-content-center">
          {page.plans.map(plan => (
            <div className="col-lg-4 mb-2 mb-lg-0" key={plan.plan_name}>
              <div className="card shadow pricing-card h-100">
                <div className="card-header border-0 bg-dark text-white py-3">
                  <h3 className="mb-2">{plan.plan_name}</h3>
                  <div>{plan.plan_subtitle}</div>
                </div>
                <div className="py-3">
                  {plan.price_line?.raw[0] &&
                    RichText.render(plan.price_line.raw, htmlSerializer)}
                </div>
                <div className="card-footer border-0 text-left flex-grow-1 py-4">
                  {plan.plan_body_text?.raw[0] &&
                    RichText.render(plan.plan_body_text.raw, htmlSerializer)}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5">
          <a className="cta" href={page.pricing_button_url}>
            {page.pricing_button_text}
          </a>
        </div>
      </Section>
      <FaqSection
        faq={{
          videos: page.videos,
          title: page.faq_title,
          button: page.faq_button_text,
          button_url: page.faq_button_url,
        }}
        locale={locale}
        link="/companies/faq"
      />
      <FeatureSection
        title={page.feature_title}
        text={page.feature_body}
        buttonText={page.feature_button_text}
        buttonUrl={page.feature_button_url}
        image={page.feature_image.localFile.childImageSharp.fluid}
      />
    </Layout>
  )
}

export const query = graphql`
  query b2bQuery($uid: String, $lang: String) {
    prismicB2BHomepage(uid: { eq: $uid }, lang: { eq: $lang }) {
      alternate_languages {
        id
        lang
        type
        uid
      }
      data {
        meta_image {
          url
        }
        meta_title
        meta_keywords
        meta_description
        hero_title
        hero_subtitle
        hero_button_text
        hero_link
        faq_button_text
        faq_button_url
        faq_title
        feature_title
        feature_body {
          raw
        }
        feature_button_text
        feature_button_url
        feature_image {
          localFile {
            childImageSharp {
              fluid(maxWidth: 700, quality: 80) {
                ...GatsbyImageSharpFluid_noBase64
              }
            }
          }
        }
        integrations_subtitle
        integrations_title
        integrations_logos {
          logo {
            url
          }
        }
        network_title
        sector_1
        sector_2
        sector_3
        diagram_text
        diagram {
          amount
          section_color
          section_name
        }
        pricing_title
        pricing_button_text
        pricing_button_url
        plans {
          plan_body_text {
            raw
          }
          plan_subtitle
          plan_name
          price_line {
            raw
          }
        }
        suite_title
        suite_subtitle
        suite_bubbles {
          bubble_body
          bubble_icon {
            url
          }
          bubble_title
          link_text
          link_url
        }
        videos {
          link
          video
        }
        testimonials_title
        testimonials {
          name
          role
          text
          image {
            localFile {
              childImageSharp {
                fixed(width: 80) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        }
        button_title
        customers_title
        partners {
          image {
            localFile {
              childImageSharp {
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid_noBase64
                }
              }
            }
          }
        }
      }
    }
  }
`

export default withPreview(B2BTemplate)
